import { useAuth } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import api from "@/lib/axiosConfig";
import { ModifiableList } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const getModifiableList = async (key: string, lang: SupportedLocale) => {
  const { data } = await api.get<ModifiableList[]>(
    `/back/api/modifiable-list?key=${key}&lang=${lang}`,
  );
  console.log(data);
  return data;
};

export const useSaneModifiableList = (key: string, lang: SupportedLocale) => {
  const { instance } = useAuth();
  return useQuery({
    queryKey: [instance, "sane-modifiable-list", key, lang],
    queryFn: () => getModifiableList(key, lang),
    enabled: !!key,
    staleTime: 1000 * 60 * 5,
  });
};

const getListData = async (key: string | undefined, lang: SupportedLocale) => {
  if (!key) {
    return [];
  }
  const { data } = await api.get<ModifiableList[]>(
    `/back/api/modifiable-list?key=${key}&lang=${lang}`,
  );
  return data.map((item) => ({
    label: item.value,
    value: item.value,
  }));
};

export const useModifiableList = (
  key: string | undefined,
  lang: SupportedLocale,
) => {
  const { instance } = useAuth();

  return useQuery({
    queryKey: [instance, "modifiable-list", key],
    queryFn: () => getListData(key, lang),
    enabled: !!key,
    staleTime: 1000 * 60 * 5,
  });
};

const createModifiableList = async (
  modifiableList: Partial<ModifiableList>,
) => {
  const { data } = await api.post(`/back/api/modifiable-list`, {
    data: modifiableList,
  });
  return data;
};

export const useCreateModifiableList = (key: string | undefined) => {
  const { instance } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createModifiableList,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [instance, "modifiable-list", key],
      });
      queryClient.invalidateQueries({
        queryKey: [instance, "sane-modifiable-list", key],
      });
    },
  });
};

const deleteModifiableList = async (id: string) =>
  await api.delete(`/back/api/modifiable-list/${id}`);

export const useDeleteModifiableList = (key: string) => {
  const { instance } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteModifiableList,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [instance, "modifiable-list", key],
      });
      queryClient.invalidateQueries({
        queryKey: [instance, "sane-modifiable-list", key],
      });
    },
  });
};

async function updateModifiableListSequence({
  newSequence,
}: {
  newSequence: string[];
}) {
  await api.patch(`/back/api/modifiable-list`, {
    sequence: newSequence,
  });
}

export const useUpdateModifiableListSequence = (key: string) => {
  const queryClient = useQueryClient();
  const { instance } = useAuth();
  return useMutation({
    mutationFn: updateModifiableListSequence,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [instance, "modifiable-list", key],
      });
      queryClient.invalidateQueries({
        queryKey: [instance, "sane-modifiable-list", key],
      });
    },
  });
};

const updateModifiableListItem = async ({
  id,
  value,
}: {
  id: string;
  value: string;
}) => {
  const { data } = await api.patch(`/back/api/modifiable-list/${id}`, {
    data: { value },
  });
  return data;
};

export const useUpdateModifiableListItem = (key: string) => {
  const { instance } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateModifiableListItem,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [instance, "modifiable-list", key],
      });
      queryClient.invalidateQueries({
        queryKey: [instance, "sane-modifiable-list", key],
      });
    },
  });
};
